import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Plus, X, Loader2, Sparkles } from "lucide-react";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CostBenefitTab({ projectId, token, costBenefit, setCostBenefit }) {
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const add = () => {
    setCostBenefit(prev => [...prev, { solution_name: "", implementation_cost: "", annual_benefit: "", one_time_benefit: "", notes: "" }]);
  };

  const update = (index, field, value) => {
    setCostBenefit(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const remove = (index) => {
    if (costBenefit.length <= 1) return;
    setCostBenefit(prev => prev.filter((_, i) => i !== index));
  };

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API}/ai/suggest-cost-benefit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!response.ok) throw new Error("AI cost-benefit generation failed");
      const data = await response.json();
      if (data.analyses) {
        setCostBenefit(data.analyses);
        toast.success(`${data.analyses.length} cost-benefit estimates generated`);
      }
    } catch (error) { toast.error(error.message); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/improve/cost-benefit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(
          costBenefit.filter(c => c.solution_name.trim()).map(c => ({
            ...c,
            implementation_cost: parseFloat(c.implementation_cost) || 0,
            annual_benefit: parseFloat(c.annual_benefit) || 0,
            one_time_benefit: parseFloat(c.one_time_benefit) || 0,
          }))
        ),
      });
      if (!response.ok) throw new Error("Failed to save");
      const data = await response.json();
      if (data.analyses) setCostBenefit(data.analyses);
      toast.success("Cost-benefit analysis saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold">Cost-Benefit Analysis</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={aiSuggest} disabled={aiLoading} data-testid="ai-suggest-cba-btn">
            {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {aiLoading ? "Generating..." : "AI Suggest"}
            <CreditCostTag cost={5} />
          </Button>
          <Button variant="outline" size="sm" onClick={add}>
            <Plus className="w-4 h-4 mr-1" /> Add Analysis
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {costBenefit.map((cba, index) => (
          <div key={index} className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <Input value={cba.solution_name} onChange={(e) => update(index, "solution_name", e.target.value)} placeholder="Solution Name" className="max-w-md font-medium" data-testid={`cba-name-${index}`} />
              {costBenefit.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => remove(index)} className="text-destructive"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Implementation Cost ($)</Label>
                <Input type="number" value={cba.implementation_cost} onChange={(e) => update(index, "implementation_cost", e.target.value)} placeholder="50000" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Annual Benefit ($)</Label>
                <Input type="number" value={cba.annual_benefit} onChange={(e) => update(index, "annual_benefit", e.target.value)} placeholder="100000" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">One-Time Benefit ($)</Label>
                <Input type="number" value={cba.one_time_benefit} onChange={(e) => update(index, "one_time_benefit", e.target.value)} placeholder="0" />
              </div>
            </div>
            {cba.payback_period_months !== undefined && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="bg-card border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">Payback Period</p>
                  <p className="text-lg font-bold">{cba.payback_period_months} mo</p>
                </div>
                <div className="bg-card border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">5-Year ROI</p>
                  <p className="text-lg font-bold text-[hsl(var(--success))]">{cba.five_year_roi}%</p>
                </div>
                <div className="bg-card border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">NPV (10%)</p>
                  <p className="text-lg font-bold">${cba.npv?.toLocaleString()}</p>
                </div>
              </div>
            )}
            <div className="mt-3">
              <Textarea value={cba.notes} onChange={(e) => update(index, "notes", e.target.value)} placeholder="Notes and assumptions..." rows={2} />
            </div>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="mt-4" data-testid="save-cba-btn">
        {saving ? "Saving..." : "Save & Calculate"}
      </Button>

      {costBenefit.some(c => c.solution_name) && (
        <ArtifactQualityReview projectId={projectId} artifactType="cost_benefit" token={token} className="mt-6" />
      )}
    </div>
  );
}
